import type * as React from "react";

import { Button, type ButtonProps } from "../../components/ui/button";
import {
  Icon,
  type IconName,
  type IconProps,
} from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import { useContentBreakpoint } from "../../hooks/use-content-size";
import type { Omittable } from "../../lib/props";

/** Icon-only counterpart for each labeled button size. */
const ICON_ONLY_SIZE: Partial<
  Record<NonNullable<ButtonProps["size"]>, ButtonProps["size"]>
> = {
  default: "icon",
  lg: "icon-lg",
  sm: "icon-sm",
  xl: "icon-xl",
  xs: "icon-xs",
};

export type ToolbarActionProps = Omit<ButtonProps, "children"> & Omittable<{
  icon: IconName;
  /** Optional override icon when the action collapses to icon-only. Defaults to `icon`. */
  collapsedIcon?: IconName;
  label: string;
  iconSize?: IconProps["size"];
}>;

export function ToolbarAction({
  icon,
  collapsedIcon,
  label,
  iconSize,
  size = "xs",
  ...buttonProps
}: ToolbarActionProps): React.ReactElement {
  const { isAtOrBelow } = useContentBreakpoint();
  const isCollapsed = isAtOrBelow("md");

  return (
    <Button
      size={isCollapsed ? (ICON_ONLY_SIZE[size ?? "default"] ?? size) : size}
      {...buttonProps}
    >
      <Icon
        name={isCollapsed ? (collapsedIcon ?? icon) : icon}
        size={iconSize}
      />
      {!isCollapsed && (
        <Text size={null} truncate>
          {label}
        </Text>
      )}
    </Button>
  );
}
